import { JSX } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { useUserData } from '../../hooks/useUserData';
import { buildCheckoutUrl } from '../../utils/billing';
import { FREE_GROUP_CHANNEL_LIMIT, FREE_GROUP_LIMIT, PRO_GROUP_CHANNEL_LIMIT } from '../../utils/limits';

interface PricingPlan {
  title: string;
  price: string;
  period: string;
  features: string[];
  buttonText: string;
  buttonVariant: 'outlined' | 'contained';
  highlighted?: boolean;
}

const proFeatures = [
  'Unlimited groups',
  `Up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group`,
  'On-demand subscriptions refresh',
];
const pricingPlans: PricingPlan[] = [
  {
    title: 'Free',
    price: '$0',
    period: 'forever',
    features: [`Organize up to ${FREE_GROUP_LIMIT} groups`, `Up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group`],
    buttonText: 'Get Started',
    buttonVariant: 'outlined',
  },
  {
    title: 'Premium',
    price: '$9.99',
    period: 'per year',
    features: proFeatures,
    buttonText: 'Upgrade to Premium',
    buttonVariant: 'contained',
    highlighted: true,
  },
  {
    title: 'Premium',
    price: '$1.59',
    period: 'per month',
    features: proFeatures,
    buttonText: 'Upgrade to Premium',
    buttonVariant: 'contained',
  },
];

const Pricing = (): JSX.Element => {
  const { data: userData, error: userDataError, isLoading, isFetching } = useUserData();
  const loadingUser = isLoading || (isFetching && !userData);
  const userError = userDataError ? userDataError.message : null;

  const billingId = userData?.billingId;
  const checkoutUrl = buildCheckoutUrl(billingId);

  return (
    <Box
      id="pricing"
      sx={{
        py: 10,
        backgroundColor: 'rgba(0,0,0,0.02)',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
          }}
        >
          Simple, Transparent Pricing
        </Typography>

        <Typography
          variant="h6"
          component="p"
          sx={{
            mb: 6,
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '700px',
            mx: 'auto',
          }}
        >
          Start for free and upgrade as your needs grow
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {pricingPlans.map((plan, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: plan.highlighted ? '2px solid' : '1px solid',
                  borderColor: plan.highlighted ? 'primary.main' : 'rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  },
                }}
              >
                {plan.highlighted && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                    {plan.title}
                  </Typography>

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h3" component="p" sx={{ fontWeight: 800 }}>
                      {plan.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <List sx={{ mb: 3 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleOutline color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ textAlign: 'center', mt: 'auto' }}>
                    <SignedOut>
                      <SignInButton mode="modal">
                        <Button
                          variant={plan.buttonVariant}
                          color="primary"
                          fullWidth
                          sx={{ py: 1.5, borderRadius: 2 }}
                        >
                          {plan.buttonText}
                        </Button>
                      </SignInButton>
                    </SignedOut>

                    <SignedIn>
                      <Button
                        variant={plan.buttonVariant}
                        color="primary"
                        fullWidth
                        sx={{ py: 1.5, borderRadius: 2 }}
                        disabled={plan.title !== 'Free' && (loadingUser || !checkoutUrl)}
                        onClick={() => {
                          if (plan.title !== 'Free' && checkoutUrl) {
                            window.location.href = checkoutUrl;
                          }
                        }}
                      >
                        {plan.buttonText}
                      </Button>
                      {plan.title !== 'Free' && (
                        <Typography
                          variant="caption"
                          color={userError ? 'error' : 'text.secondary'}
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {userError
                            ? userError
                            : loadingUser
                              ? 'Preparing checkout…'
                              : checkoutUrl
                                ? 'You will be redirected to secure checkout.'
                                : 'Generating your checkout link…'}
                        </Typography>
                      )}
                    </SignedIn>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Pricing;
